import './SourceCodingPanel.css';

// ─────────────────────────────────────────────────────────────────────────────
// What the source system said this visit billed (2026-10-13 amendment, A5).
//
// A facility whose coders assign the code (Facility A) often sends NO procedure
// code; it sends `charge_lines` instead — closed-vocabulary keys such as
// `clinic_visit` with a quantity and the facility's own amount. This panel shows
// them, with the code an admin mapped each key to, so the coder knows what to code
// without reading clinical notes.
//
// INFORMATION ONLY: nothing here is billed, and nothing is copied into the service
// lines — the coder assigns every code. The claim total is still the facility's own
// price (`self_pay_source_charge`, shown under Financial Summary).
// ─────────────────────────────────────────────────────────────────────────────

const money = (value) => `$${Number(value).toFixed(2)}`;

function SourceCodingPanel({ sourceCoding }) {
  if (!sourceCoding) return null;
  const lines = sourceCoding.charge_lines ?? [];
  const code = sourceCoding.procedure_code;
  if (!code && lines.length === 0) return null;

  const unmapped = lines.filter((line) => !line.mapped).length;

  return (
    <section className="scp-panel" aria-label="From the source system">
      <p className="scp-intro">
        <strong>From the source system</strong> — what this visit billed, to guide coding. Nothing here is billed or
        copied into the lines below.
      </p>
      <p className="scp-code-row">
        Provisional code:{' '}
        {code ? (
          <><span className="scp-badge">{code}</span>{sourceCoding.procedure_code_system && <span className="scp-muted"> {sourceCoding.procedure_code_system}</span>}</>
        ) : (
          <span className="scp-muted">none sent — assign the codes</span>
        )}
      </p>

      {lines.length > 0 && (
        <table className="scp-table">
          <thead>
            <tr>
              <th>Billed service</th>
              <th className="scp-num">Qty</th>
              <th className="scp-num">Amount</th>
              <th>Suggested code</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.line_number}>
                <td><span className="scp-badge">{line.service_key}</span></td>
                <td className="scp-num">{line.quantity}</td>
                <td className="scp-num">{money(line.amount)}</td>
                <td>
                  {line.mapped ? (
                    <><span className="scp-badge">{line.suggested_code}</span><span className="scp-muted"> {line.suggested_code_system}</span></>
                  ) : (
                    <span className="scp-unmapped">Unmapped</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {unmapped > 0 && (
        <p className="scp-muted scp-footnote">
          {unmapped} service key{unmapped === 1 ? ' has' : 's have'} no suggested code yet. An admin can map
          {unmapped === 1 ? ' it' : ' them'} under Settings → CPT / HCPCS.
        </p>
      )}
    </section>
  );
}

export default SourceCodingPanel;
